/**
 * useAgentSession — SSE transport for a Managed Agent session.
 *
 * Connects an `EventSource` to the backend's
 * `GET /api/v2/agent-sessions/:id/stream` endpoint, accumulates events into
 * typed state, and exposes actions to send messages / interrupt / finalize.
 *
 * Events the backend forwards (shape documented in
 * apps/app-backend/routes/v2/agentSessions.js):
 *   - agent.message            { content: [...] }
 *   - agent.tool_use           { name }                    (built-in tools)
 *   - custom_tool_request      { name, input, id }         (our tools)
 *   - custom_tool_result       { name, result, id }
 *   - session.status_idle      { stop_reason }
 *   - session.status_running
 *   - session.status_terminated
 *   - session.finalized        { approved_plan_gcs_path }
 *   - usage                    { model_usage }
 *   - error                    { message }
 *
 * Reconnect is DIY — if the EventSource's onerror fires, we set `connectionError`
 * and the component can call reconnect() to reopen. We do NOT auto-reconnect
 * in v1 to keep semantics clear.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';

const STATUS = {
  INITIALIZING: 'initializing',
  STREAMING: 'streaming',
  IDLE: 'idle',
  WAITING_FOR_USER: 'waiting_for_user',
  FINALIZED: 'finalized',
  TERMINATED: 'terminated',
  ERROR: 'error',
};

export function useAgentSession(sessionId, accountId, userId) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(sessionId ? STATUS.INITIALIZING : STATUS.IDLE);
  const [connectionError, setConnectionError] = useState(null);
  const [approvedPlanGcsPath, setApprovedPlanGcsPath] = useState(null);
  const [usage, setUsage] = useState({ input_tokens: 0, output_tokens: 0, cache_read_input_tokens: 0 });
  const eventSourceRef = useRef(null);
  // Tracks Anthropic event IDs we've already processed (history + live).
  // Used to dedup the live tail against the just-fetched history.
  const seenEventIds = useRef(new Set());

  // Fetch history (persisted messages) and replay them into the event log
  // BEFORE opening the live SSE tail. This is what makes resuming a prior
  // session show the prior conversation immediately.
  const loadHistory = useCallback(async () => {
    if (!sessionId) return;
    try {
      const resp = await api.get(
        getApiUrl(`/api/v2/agent-sessions/${sessionId}/events`),
        { params: { accountId, userId } },
      );
      const history = (resp.data?.events || []).map(e => ({
        kind: e.kind,
        text: e.text,
        ts: e.ts,
        ...(e.event_id ? { id: e.event_id } : {}),
      }));
      // Track all history event IDs so the live tail can dedup. Keyed
      // by `${type}:${id}` to match the live-path dedup below — see the
      // comment there for why type needs to be part of the key. History
      // entries are persisted agent messages, whose live type is
      // `agent.message`.
      for (const h of history) {
        if (h.id) seenEventIds.current.add(`agent.message:${h.id}`);
      }
      if (history.length > 0) {
        setEvents(prev => [...history, ...prev]);
      }
    } catch (err) {
      // History fetch failure is non-fatal — log + continue with live only.
      console.warn('[useAgentSession] history fetch failed:', err.message);
    }
  }, [sessionId, accountId, userId]);

  // Open/re-open the SSE stream.
  const connect = useCallback(() => {
    if (!sessionId) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    setConnectionError(null);
    setStatus(STATUS.STREAMING);

    // EventSource can't send custom headers — pass the API key as a query
    // param. Backend middleware accepts ?apiKey= as a fallback to x-api-key.
    const apiKey = process.env.REACT_APP_API_KEY || '';
    const url = getApiUrl(
      `/api/v2/agent-sessions/${sessionId}/stream`
      + `?accountId=${encodeURIComponent(accountId)}`
      + `&userId=${encodeURIComponent(userId)}`
      + `&apiKey=${encodeURIComponent(apiKey)}`,
    );
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (msg) => {
      let data;
      try { data = JSON.parse(msg.data); } catch (e) { return; }
      // Defensive: drop malformed events (no type, or empty type).
      // SSE comments (heartbeats) never reach onmessage per spec, but
      // an upstream proxy that mangles the stream into a JSON wrapper
      // could leak something untyped here.
      if (!data || typeof data.type !== 'string' || data.type.length === 0) return;
      // Dedup: skip events we already replayed from history. Keyed by
      // (type, id) because custom_tool_request and custom_tool_result
      // intentionally share the same tool_use_id — keying on id alone
      // caused the result event to be swallowed, leaving the matching
      // request entry stuck at status='running' forever (which kept the
      // Copilot input disabled after every tool call).
      const dedupKey = data.id ? `${data.type}:${data.id}` : null;
      if (dedupKey && seenEventIds.current.has(dedupKey)) return;
      if (dedupKey) seenEventIds.current.add(dedupKey);
      handleEvent(data, setEvents, setStatus, setApprovedPlanGcsPath, setUsage);
    };

    es.onerror = () => {
      // EventSource auto-reconnects on transient errors. For fatal (4xx,
      // session gone), onerror fires and readyState becomes CLOSED.
      if (es.readyState === EventSource.CLOSED) {
        setConnectionError('Stream closed');
        setStatus(STATUS.ERROR);
      }
    };
  }, [sessionId, accountId, userId]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    // New session mounted — wipe any state the previous session left behind.
    // Without this, clicking "New Session" in LaunchBar would overlay the
    // fresh greeting on top of the old conversation, edits, and usage count.
    // On first mount the state is already at defaults so these are no-ops.
    setEvents([]);
    setStatus(STATUS.INITIALIZING);
    setConnectionError(null);
    setApprovedPlanGcsPath(null);
    setUsage({ input_tokens: 0, output_tokens: 0, cache_read_input_tokens: 0 });
    seenEventIds.current = new Set();

    (async () => {
      // History first, then live tail. Stream-after-history avoids the user
      // seeing live events appear above their replayed history.
      await loadHistory();
      if (!cancelled) connect();
    })();
    return () => {
      cancelled = true;
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [sessionId, loadHistory, connect]);

  // Actions
  const sendMessage = useCallback(async (text) => {
    if (!sessionId) return;
    setEvents(prev => [...prev, { kind: 'user_message', text, ts: Date.now() }]);
    setStatus(STATUS.STREAMING);
    await api.post(
      getApiUrl(`/api/v2/agent-sessions/${sessionId}/message`),
      { text, accountId, userId },
    );
  }, [sessionId, accountId, userId]);

  const interrupt = useCallback(async () => {
    if (!sessionId) return;
    await api.post(
      getApiUrl(`/api/v2/agent-sessions/${sessionId}/interrupt`),
      { accountId, userId },
    );
  }, [sessionId, accountId, userId]);

  const reconnect = useCallback(() => { connect(); }, [connect]);

  // Derived views over the event stream that components want to render.
  const derived = useMemo(() => deriveFromEvents(events), [events]);

  return {
    status,
    events,           // raw event history (debug / copilot sidebar)
    messages: derived.messages,  // agent + user messages, in order
    toolCalls: derived.toolCalls, // custom tool call history
    issues: derived.issues,       // report_issue entries
    currentPlan: derived.currentPlan,  // authoritative DV360/TTD final plan
    warnings: derived.warnings,   // latest validate_plan warnings
    approvedPlanGcsPath,
    usage,
    connectionError,
    sendMessage,
    interrupt,
    reconnect,
  };
}

// -----------------------------------------------------------------------------
// Event routing
// -----------------------------------------------------------------------------

function handleEvent(evt, setEvents, setStatus, setApprovedPlanGcsPath, setUsage) {
  switch (evt.type) {
    case 'agent.message': {
      const text = (evt.content || [])
        .filter(b => b.type === 'text')
        .map(b => b.text)
        .join('');
      setEvents(prev => [...prev, { kind: 'agent_message', id: evt.id, text, ts: Date.now() }]);
      break;
    }
    case 'agent.tool_use':
      setEvents(prev => [...prev, { kind: 'builtin_tool_use', id: evt.id, name: evt.name, ts: Date.now() }]);
      break;
    case 'custom_tool_request':
      setEvents(prev => [...prev, {
        kind: 'custom_tool_request',
        id: evt.id,
        name: evt.name,
        input: evt.input,
        status: 'running',
        ts: Date.now(),
      }]);
      break;
    case 'custom_tool_result':
      setEvents(prev => prev.map(e => (
        e.kind === 'custom_tool_request' && e.id === evt.id
          ? { ...e, status: 'done', result: evt.result }
          : e
      )));
      break;
    case 'plan.snapshot':
      // Backend pushed the final plan (DV360/TTD shape). Dashboard and
      // edit paths both operate on this single source of truth.
      setEvents(prev => [...prev, { kind: 'plan_snapshot', plan: evt.plan, ts: Date.now() }]);
      break;
    case 'plan.snapshot.error':
      setEvents(prev => [...prev, {
        kind: 'error',
        message: `Couldn't load plan: ${evt.message}`,
        ts: Date.now(),
      }]);
      break;
    case 'session.status_idle': {
      const reason = evt.stop_reason?.type;
      setStatus(reason === 'requires_action' ? STATUS.WAITING_FOR_USER : STATUS.IDLE);
      break;
    }
    case 'session.status_running':
      setStatus(STATUS.STREAMING);
      break;
    case 'session.status_terminated':
      setStatus(STATUS.TERMINATED);
      break;
    case 'session.finalized':
      setStatus(STATUS.FINALIZED);
      setApprovedPlanGcsPath(evt.approved_plan_gcs_path);
      break;
    case 'usage':
      if (evt.model_usage) {
        setUsage(prev => ({
          input_tokens: prev.input_tokens + (evt.model_usage.input_tokens || 0),
          output_tokens: prev.output_tokens + (evt.model_usage.output_tokens || 0),
          cache_read_input_tokens:
            prev.cache_read_input_tokens + (evt.model_usage.cache_read_input_tokens || 0),
        }));
      }
      break;
    case 'error':
      setEvents(prev => [...prev, { kind: 'error', message: evt.message, ts: Date.now() }]);
      setStatus(STATUS.ERROR);
      break;
    default:
      // Unknown / raw — append to history for debug visibility.
      setEvents(prev => [...prev, { kind: 'unknown', raw: evt, ts: Date.now() }]);
  }
}

// -----------------------------------------------------------------------------
// Selectors over event history
// -----------------------------------------------------------------------------

function deriveFromEvents(events) {
  const messages = events.filter(
    e => e.kind === 'agent_message' || e.kind === 'user_message',
  );
  const toolCalls = events.filter(e => e.kind === 'custom_tool_request');

  // Latest plan: prefer the most recent plan.snapshot. `currentPlan` is
  // the authoritative DV360/TTD final plan.
  let currentPlan = null;
  for (const e of events) {
    if (e.kind === 'plan_snapshot' && e.plan) {
      currentPlan = e.plan;
    }
  }
  if (!currentPlan) {
    for (const call of toolCalls) {
      if (call.status !== 'done' || !call.result) continue;
      if (call.name === 'read_pipeline_artifact'
          && call.input?.artifact === 'final_plan'
          && call.result?.content) {
        currentPlan = call.result.content;
      }
    }
  }
  // apply_plan_edit currently returns diff only, not the full plan. TODO:
  // extend the executor to return the full new plan so we can fold it here
  // instead of re-fetching from GCS.

  // Latest warnings = last validate_plan result, or aggregated from issues
  // emitted via apply_plan_edit new_warnings.
  let warnings = [];
  for (let i = toolCalls.length - 1; i >= 0; i--) {
    const c = toolCalls[i];
    if (c.name === 'validate_plan' && c.status === 'done' && c.result?.issues) {
      warnings = c.result.issues;
      break;
    }
  }

  // Issues (from report_issue) — until the agent has a dedicated
  // report_issue tool wired, we surface warnings-with-severity from
  // validate_plan here. Follow-up: add a real report_issue tool + handler.
  const issues = warnings;

  return { messages, toolCalls, issues, currentPlan, warnings };
}

export { STATUS };
